/**
 * @file BillCostTypeDropdown.js
 * @description A dropdown component for selecting a billing cost type.
 */

import React, { useState, useEffect } from 'react';
import { fetchBillCostTypes } from '../../../services/systemService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';

/**
 * A dropdown component for selecting a billing cost type.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label should be displayed inline.
 * @param {number} props.selectedCostType - The ID of the selected bill cost type.
 * @param {Function} props.onCostTypeChange - The callback function to be called when the selection changes.
 * @param {string} [props.label="Cost Type"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @returns {JSX.Element} The rendered BillCostTypeDropdown component.
 */
const BillCostTypeDropdown = ({
    className,
    inlineLabel = true,
    selectedCostType,
    onCostTypeChange,
    label = 'Cost Type',
    isRequired = false,
}) => {
    const [costTypes, setCostTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getCostTypes = async () => {
            setIsLoading(true);
            try {
                const data = await fetchBillCostTypes();
                setCostTypes(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Could not fetch bill cost types:', error);
                Toast.error('Unable to load cost types.');
            } finally {
                setIsLoading(false);
            }
        };

        getCostTypes();
    }, []);

    const getDefaultLabel = () => {
        const matchingCostType = costTypes.find(
            costType => costType.id === selectedCostType,
        );
        return matchingCostType ? matchingCostType.name : 'Select Cost Type';
    };

    const costTypeOptions = costTypes.map(costType => ({
        value: costType.id,
        label: costType.name,
    }));

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <label htmlFor="bill-cost-type-dropdown">
                {label}
                {isRequired && <span className="text-danger">*</span>}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    id="bill-cost-type-dropdown"
                    defaultValue={getDefaultLabel()}
                    options={costTypeOptions}
                    value={selectedCostType}
                    onChange={onCostTypeChange}
                    disabled={costTypes.length === 0 || isLoading}
                />
            </LoadingWrapper>
        </div>
    );
};

export default BillCostTypeDropdown;
